'use client'

import * as React from 'react'
import { LuCheck, LuChevronDown, LuSearch } from 'react-icons/lu'
import { TbTool } from 'react-icons/tb'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import type { ToolType, ToolsSelection } from '@/data/types/analytics.types'

// Frontend tool types (simplified - code_execution represents both code_interpreter and code_execution on backend)
type FrontendToolType = 'web_search' | 'code_execution' | 'no_tools'

interface ToolOption {
  value: FrontendToolType
  label: string
  description: string
}

// Human-readable labels and descriptions for tools shown in the UI
// Note: code_execution in the UI maps to both code_interpreter AND code_execution on the backend
const toolOptions: ToolOption[] = [
  { value: 'web_search', label: 'Web Search', description: 'Internet search queries' },
  { value: 'code_execution', label: 'Code Execution', description: 'Code interpreter and execution' },
  { value: 'no_tools', label: 'No Tools', description: 'Transactions without any tools' },
]

// Map frontend tool selection to backend tool types
// code_execution in UI maps to both code_interpreter and code_execution in backend
const mapToBackendTools = (frontendTools: FrontendToolType[]): ToolType[] => {
  const backendTools: ToolType[] = []
  for (const tool of frontendTools) {
    if (tool === 'code_execution') {
      // Map to both backend values
      backendTools.push('code_interpreter', 'code_execution')
    } else {
      backendTools.push(tool)
    }
  }
  return backendTools
}

// Internal selection state uses frontend tool types
interface FrontendToolsSelection {
  all: boolean
  tools: FrontendToolType[]
}

interface ToolsSelectorProps {
  value?: ToolsSelection
  onChange?: (selection: ToolsSelection) => void
  className?: string
}

// Default selections
export const ALL_TOOLS_SELECTION: ToolsSelection = { all: true, tools: [] }
export const NO_TOOLS_SELECTION: ToolsSelection = { all: false, tools: [] }

const ToolsSelector = ({
  value: externalValue,
  onChange: externalOnChange,
  className,
}: ToolsSelectorProps) => {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')

  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = React.useState<FrontendToolsSelection>({ all: true, tools: [] })

  // Convert external ToolsSelection to frontend selection
  // (backend tools code_interpreter/code_execution both map to frontend code_execution)
  const toFrontendSelection = (selection: ToolsSelection): FrontendToolsSelection => {
    if (selection.all) return { all: true, tools: [] }

    const frontendTools: FrontendToolType[] = []
    for (const tool of selection.tools) {
      if (tool === 'code_interpreter' || tool === 'code_execution') {
        if (!frontendTools.includes('code_execution')) {
          frontendTools.push('code_execution')
        }
      } else if (tool === 'web_search' || tool === 'no_tools') {
        if (!frontendTools.includes(tool)) {
          frontendTools.push(tool)
        }
      }
      // Ignore file_search and file_storage
    }
    return { all: false, tools: frontendTools }
  }

  // Convert frontend selection to backend ToolsSelection
  const toBackendSelection = (selection: FrontendToolsSelection): ToolsSelection => {
    if (selection.all) return ALL_TOOLS_SELECTION
    return { all: false, tools: mapToBackendTools(selection.tools) }
  }

  // Use external value if provided, otherwise use internal state
  const frontendValue = externalValue !== undefined
    ? toFrontendSelection(externalValue)
    : internalValue

  const handleChange = (newFrontendValue: FrontendToolsSelection) => {
    if (externalOnChange) {
      externalOnChange(toBackendSelection(newFrontendValue))
    } else {
      setInternalValue(newFrontendValue)
    }
  }

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery.trim()) return toolOptions
    const query = searchQuery.toLowerCase()
    return toolOptions.filter(
      opt => opt.label.toLowerCase().includes(query) || opt.description.toLowerCase().includes(query)
    )
  }, [searchQuery])

  // Check selection states
  const allTools = toolOptions.map(opt => opt.value)
  const isAllSelected = frontendValue.all || (allTools.length > 0 && frontendValue.tools.length === allTools.length)
  const isNoneSelected = !frontendValue.all && frontendValue.tools.length === 0
  const isSomeSelected = !isNoneSelected && !isAllSelected
  const selectedCount = frontendValue.all ? allTools.length : frontendValue.tools.length

  // Get display text for the button
  const getDisplayText = () => {
    if (isAllSelected) return 'All Tools'
    if (isNoneSelected) return 'No Tools Filter'
    if (frontendValue.tools.length === 1) {
      const option = toolOptions.find(opt => opt.value === frontendValue.tools[0])
      return option?.label || 'Tool'
    }
    return `${selectedCount} Tools`
  }

  // Toggle Select All
  const toggleSelectAll = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (isAllSelected) {
      handleChange({ all: false, tools: [] })
    } else {
      handleChange({ all: true, tools: [] })
    }
  }

  // Toggle individual tool selection
  const toggleTool = (tool: FrontendToolType, e?: React.MouseEvent) => {
    e?.stopPropagation()

    if (frontendValue.all) {
      // If all is selected, switching to all except this one
      const newTools = allTools.filter(t => t !== tool)
      handleChange({ all: false, tools: newTools })
      return
    }

    const isSelected = frontendValue.tools.includes(tool)
    let newTools: FrontendToolType[]

    if (isSelected) {
      // Remove this tool
      newTools = frontendValue.tools.filter(t => t !== tool)
    } else {
      // Add this tool
      newTools = [...frontendValue.tools, tool]
    }

    // Check if all tools are now selected
    if (newTools.length === allTools.length) {
      handleChange({ all: true, tools: [] })
    } else {
      handleChange({ all: false, tools: newTools })
    }
  }

  // Clear all selections
  const clearAll = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    handleChange({ all: false, tools: [] })
  }

  // Check if a tool is selected
  const isToolSelected = (tool: FrontendToolType) => {
    return frontendValue.all || frontendValue.tools.includes(tool)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="transparent-outline"
          size="xs"
          className={cn(
            'hover:bg-[#F2EEFF] hover:text-[#654EDA] hover:border-[#E2DAFF]',
            'focus-visible:ring-[#654EDA]/20 focus-visible:border-[#654EDA] focus-visible:ring-2',
            open && 'bg-[#E2DAFF] text-[#654EDA] border-[#C6B9FF] hover:bg-[#E2DAFF] focus-visible:ring-0',
            className
          )}
        >
          <TbTool className="size-4" />
          {getDisplayText()}
          <LuChevronDown
            className={cn(
              'size-3.5 ml-0.5 transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0 border-[#EDEEF3] shadow-lg flex flex-col max-h-[var(--radix-popover-content-available-height,400px)]"
        align="start"
        sideOffset={6}
      >
        {/* Header with Search and Clear */}
        <div className="p-3 border-b border-[#EDEEF3] shrink-0">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#8A8F98]" />
              <input
                type="text"
                placeholder="Search tools..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-[30px] pl-9 pr-3 rounded-[6px] border border-[#EDEEF3] bg-white text-[13px] text-[#08090A] placeholder:text-[#8A8F98] hover:border-[#D1D5DB] focus:outline-none focus:border-[#654EDA] transition-colors"
              />
            </div>
            {selectedCount > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="text-[12px] text-[#8A8F98] hover:text-[#654EDA] transition-colors whitespace-nowrap cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 min-h-0" onWheel={(e) => e.stopPropagation()}>
          {filteredOptions.length === 0 ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              No tools found
            </div>
          ) : (
            <>
              {/* Select All Option */}
              {!searchQuery && (
                <div
                  className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#F7F8F8] transition-colors border-b border-[#EDEEF3]"
                  onClick={toggleSelectAll}
                >
                  <Checkbox
                    checked={isAllSelected ? true : isSomeSelected ? "indeterminate" : false}
                    onCheckedChange={() => toggleSelectAll()}
                    className="size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA] data-[state=indeterminate]:bg-[#654EDA] data-[state=indeterminate]:border-[#654EDA] cursor-pointer"
                  />
                  <span className="text-[13px] font-medium text-[#08090A]">
                    Select All
                  </span>
                  <span className="ml-auto text-[12px] text-[#8A8F98]">
                    {selectedCount}/{allTools.length}
                  </span>
                </div>
              )}

              {/* Tool Options */}
              {filteredOptions.map((option) => {
                const isSelected = isToolSelected(option.value)
                const isNoToolsOption = option.value === 'no_tools'

                return (
                  <div
                    key={option.value}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#F7F8F8] transition-colors",
                      isNoToolsOption && "border-t border-[#EDEEF3]"
                    )}
                    onClick={(e) => toggleTool(option.value, e)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleTool(option.value)}
                      className="size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA] cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <span className={cn(
                        'text-[13px] block',
                        isSelected ? 'text-[#08090A] font-medium' : 'text-[#08090A]',
                        isNoToolsOption && 'italic'
                      )}>
                        {option.label}
                      </span>
                      <span className="text-[11px] text-[#8A8F98] block truncate">
                        {option.description}
                      </span>
                    </div>
                    {isSelected && (
                      <LuCheck className="size-4 text-[#654EDA] shrink-0" />
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { ToolsSelector }
export type { ToolsSelection }
